// src/utils/stampaSopralluogo.js
// Verbale di sopralluogo da stampare o salvare in PDF (ottobre 2026, W407). Una sola funzione
// per la commessa (components/SopralluogoTab.js) e per il Registro Sopralluoghi: il verbale è
// identico dovunque lo si apra. «Salva come PDF» è la stampa del browser.
import { dominioSito } from "./fornitore";

const STATI_SOP = {
  bozza:      { label: "Bozza",      bg: "#f3f4f6", col: "#6b7280" },
  completato: { label: "Completato", bg: "#d1fae5", col: "#065f46" },
  firmato:    { label: "Firmato",    bg: "#dbeafe", col: "#2563eb" },
};
const esc = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** Apre il verbale in una nuova finestra con il pulsante «Stampa». Restituisce false se il browser blocca la finestra. */
export function stampaSopralluogo(s, orgInfo, projectName = "") {
  const stato = STATI_SOP[s.stato] || STATI_SOP.bozza;
  // senza commessa (W416): al posto della commessa il cliente o potenziale cliente
  const commessa = !!String(s.projectId || "").trim();
  const nomeCommessa = projectName || s.projectName || "";
  const win = window.open("", "_blank");
  if (!win) return false;
  const logoHtml = orgInfo?.logo
    ? `<img src="${orgInfo.logo}" style="height:44px;object-fit:contain;"/>`
    : `<div style="font-size:18px;font-weight:900;color:#2563eb;">◆ ${esc(orgInfo?.nome || orgInfo?.name || "Nexiora")}</div>`;
  const anomalieHtml = (s.anomalie || []).filter(a => a.descrizione).map((a, i) =>
    `<tr style="background:${i%2===0?"#fff":"#fafafa"}">
      <td style="padding:5px 8px;border:1px solid #e5e7eb;">${esc(a.descrizione)}</td>
      <td style="padding:5px 8px;border:1px solid #e5e7eb;text-align:center;color:${a.gravita==="alta"?"#dc2626":a.gravita==="media"?"#d97706":"#059669"};font-weight:700;">${a.gravita||"media"}</td>
    </tr>`
  ).join("") || `<tr><td colspan="2" style="padding:5px 8px;color:#9ca3af;">Nessuna anomalia rilevata</td></tr>`;
  const fotoHtml = (s.foto||[]).length>0
    ? `<div style="margin-top:8px;display:flex;flex-wrap:wrap;gap:6px;">${(s.foto||[]).map(f=>`<img src="${f.data}" style="width:100px;height:100px;object-fit:cover;border-radius:4px;border:1px solid #e5e7eb;"/>`).join("")}</div>`
    : "";

  win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
  <title>Verbale Sopralluogo ${esc(s.numero||"")}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box;}
    body{font-family:Arial,sans-serif;font-size:11px;color:#111;padding:16mm 20mm;}
    @media screen{body{max-width:800px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
    .hdr{border-bottom:3px solid #2563eb;padding-bottom:10px;margin-bottom:14px;display:flex;justify-content:space-between;align-items:flex-start;}
    .title{font-size:15px;font-weight:800;color:#1d4ed8;}
    .numero{font-size:18px;font-weight:900;color:#1d4ed8;font-family:monospace;}
    .badge{display:inline-block;padding:3px 10px;border-radius:20px;font-size:10px;font-weight:700;background:${stato.bg};color:${stato.col};}
    .cbox{background:#eff6ff;border:2px solid #93c5fd;border-radius:8px;padding:10px 14px;margin-bottom:12px;}
    h3{font-size:10px;font-weight:700;color:#1d4ed8;text-transform:uppercase;letter-spacing:.04em;background:#eff6ff;padding:5px 8px;border-radius:4px;margin-bottom:8px;}
    .g2{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:12px;}
    .lbl{font-size:9px;color:#6b7280;font-weight:600;display:block;margin-bottom:2px;}
    .val{font-size:11px;font-weight:500;}
    .text-block{white-space:pre-wrap;line-height:1.6;font-size:11px;margin-bottom:10px;padding:8px;background:#f9fafb;border-radius:6px;}
    table{width:100%;border-collapse:collapse;margin-bottom:10px;}
    th{background:#2563eb;color:#fff;padding:5px 8px;font-size:9px;}
    td{padding:5px 8px;border:1px solid #e5e7eb;font-size:11px;}
    .fbox{border:1px solid #e5e7eb;border-radius:6px;padding:10px;min-height:70px;display:flex;align-items:center;justify-content:center;}
    .footer{border-top:1px solid #e5e7eb;margin-top:14px;padding-top:8px;font-size:9px;color:#9ca3af;display:flex;justify-content:space-between;}
    @media print{body{padding:10mm 12mm;}}
  </style></head><body>
  <div class="hdr">
    <div>
      ${logoHtml}
      <div style="font-size:11px;font-weight:700;color:#374151;margin-top:3px;">${esc(orgInfo?.nome||orgInfo?.name||"")}</div>
      ${orgInfo?.indirizzo?`<div style="font-size:9px;color:#6b7280;">${esc(orgInfo.indirizzo)}</div>`:""}
      ${orgInfo?.piva?`<div style="font-size:9px;color:#6b7280;">P.IVA ${esc(orgInfo.piva)}</div>`:""}
    </div>
    <div style="text-align:right;">
      <div class="title">VERBALE DI SOPRALLUOGO</div>
      <div class="numero">${esc(s.numero||"—")}</div>
      <div style="margin-top:4px;"><span class="badge">${stato.label}</span></div>
    </div>
  </div>
  <div class="cbox">
    ${commessa ? `<div style="font-size:9px;color:#2563eb;font-weight:700;text-transform:uppercase;margin-bottom:3px;">Commessa</div>
    <div style="font-size:13px;font-weight:800;color:#1d4ed8;">${s.codiceCommessa?`[${esc(s.codiceCommessa)}] `:""}${esc(nomeCommessa)}</div>
    ${s.clienteNome?`<div style="font-size:11px;color:#6b7280;margin-top:2px;">Cliente: ${esc(s.clienteNome)}</div>`:""}`
    : `<div style="font-size:9px;color:#2563eb;font-weight:700;text-transform:uppercase;margin-bottom:3px;">${s.clienteId ? "Cliente" : "Potenziale cliente"}</div>
    <div style="font-size:13px;font-weight:800;color:#1d4ed8;">${esc(s.clienteNome || s.nomeCliente || "—")}</div>
    <div style="font-size:11px;color:#6b7280;margin-top:2px;">Sopralluogo prima dell'apertura della commessa</div>`}
    ${s.rcNumero?`<div style="font-size:11px;color:#6b7280;margin-top:2px;">Richiesta Commerciale: ${esc(s.rcNumero)}</div>`:""}
  </div>
  <div class="g2">
    <div>
      <span class="lbl">Data</span><span class="val">${s.data?new Date(s.data+"T00:00:00").toLocaleDateString("it-IT",{weekday:"long",day:"numeric",month:"long",year:"numeric"}):"—"}</span><br/>
      <span class="lbl" style="margin-top:6px;display:block;">Ora</span><span class="val">${esc(s.ora||"—")}</span><br/>
      <span class="lbl" style="margin-top:6px;display:block;">Luogo</span><span class="val">${esc(s.luogo||"—")}</span>
    </div>
    <div>
      <span class="lbl">Personale aziendale</span><span class="val">${esc((s.personalePresente||[]).join(", ")||"—")}</span><br/>
      ${s.altrePersone?`<span class="lbl" style="margin-top:6px;display:block;">Altre persone presenti</span><span class="val">${esc(s.altrePersone)}</span>`:""}
    </div>
  </div>
  ${s.motivoSopralluogo?`<h3>Motivo del sopralluogo</h3><div class="text-block">${esc(s.motivoSopralluogo)}</div>`:""}
  ${s.situazioneRilevata?`<h3>Situazione rilevata</h3><div class="text-block">${esc(s.situazioneRilevata)}</div>`:""}
  <h3>Anomalie e criticità riscontrate</h3>
  <table><thead><tr><th>Anomalia / Criticità</th><th style="width:100px;">Gravità</th></tr></thead><tbody>${anomalieHtml}</tbody></table>
  ${s.prescrizioni?`<h3>Prescrizioni e raccomandazioni</h3><div class="text-block">${esc(s.prescrizioni)}</div>`:""}
  ${s.attivitaDaEseguire?`<h3>Attività da eseguire</h3><div class="text-block">${esc(s.attivitaDaEseguire)}</div>`:""}
  ${s.materialiNecessari?`<h3>Materiali / Mezzi necessari</h3><div class="text-block">${esc(s.materialiNecessari)}</div>`:""}
  ${s.note?`<h3>Note</h3><div class="text-block">${esc(s.note)}</div>`:""}
  ${fotoHtml?`<h3>Foto</h3>${fotoHtml}`:""}
  <h3>Firma</h3>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-top:6px;">
    <div>
      <div style="font-size:10px;color:#6b7280;margin-bottom:4px;">Redatto da: ${esc((s.personalePresente||[])[0]||"—")}</div>
      <div class="fbox">
        ${s.firma?`<img src="${s.firma}" style="max-height:70px;max-width:220px;"/>`:`<div style="color:#9ca3af;font-size:11px;">Firma non raccolta</div>`}
        ${s.nomeFirmatario?`<div style="font-size:10px;margin-top:4px;">${esc(s.nomeFirmatario)}</div>`:""}
      </div>
    </div>
    <div>
      <div style="font-size:10px;color:#6b7280;margin-bottom:4px;">Per presa visione (cliente/DL)</div>
      <div class="fbox"><div style="color:#9ca3af;font-size:11px;">Firma</div></div>
    </div>
  </div>
  <div class="footer">
    <span>Generato da Nexiora — ${dominioSito()}</span>
    <span>${commessa ? `${s.codiceCommessa?`[${esc(s.codiceCommessa)}] `:""}${esc(nomeCommessa)}` : esc(s.clienteNome || s.nomeCliente || "")}</span>
    <span>Stampato il ${new Date().toLocaleDateString("it-IT")}</span>
  </div>
  
  </body></html>`);
  win.document.close();
  setTimeout(()=>{
    if (win.closed) return;
    const style = win.document.createElement("style");
    style.textContent = "@media print { .no-print-btn { display:none !important; } }";
    win.document.head.appendChild(style);
    const btn = win.document.createElement("button");
    btn.textContent = "🖨️ Stampa";
    btn.className = "no-print-btn";
    btn.onclick = () => win.print();
    btn.style.cssText = "position:fixed;top:16px;right:16px;z-index:9999;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,.2);";
    win.document.body.appendChild(btn);
  }, 50);
  return true;
}
